import React, { useState } from 'react';

import CloseIcon from 'icons/ic-close.svg';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import useBodyOverflowToggle from 'components/hooks/useBodyOverflowToggle';
import useEscapeKey from 'components/hooks/useEscapeKey';

import OutlinedButton from 'components/application/buttons/OutlinedButton';

import { useCurrentActor } from 'components/contexts/CurrentActor';
import GeneratingExportModal from 'components/exports/components/GeneratingExportModal';
import ProjectsTable from 'components/projectsAnalytics/components/ProjectsTable';

import { resourceShape } from 'components/helpers/serialisableResources';

export default function ProjectsModal({
  domainDashboardReport,
  hasNextPage,
  isGeneratingExport,
  onClose,
  onExportClick,
  onFetchNextPage,
  onProjectClick,
  selections,
  subtitle,
  title,
}) {
  const {
    division: {
      attributes: { pluralRAMSName },
    },
  } = useCurrentActor();

  const [isGeneratingExportModalOpen, setIsGeneratingExportModalOpen] =
    useState(false);

  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'projectsAnalytics.components.ProjectsModal',
  });

  const { t: i18nTShared } = useTranslation('common', { keyPrefix: 'shared' });

  useBodyOverflowToggle(true);
  useEscapeKey(onClose, [true]);

  const handleModalWrapperClick = (event) => {
    if (event.target.dataset.elementName === 'projects-modal-modal-wrapper') {
      onClose();
    }
  };

  const handleTableWrapperScroll = ({ target }) => {
    const isScrollBottom =
      Math.abs(target.scrollHeight - target.clientHeight - target.scrollTop) <
      1;

    if (isScrollBottom && hasNextPage) onFetchNextPage();
  };

  const projectCount =
    domainDashboardReport?.data.relationships.projects.data.length;

  return (
    <>
      <div className='tw-fixed tw-bottom-0 tw-left-0 tw-right-0 tw-top-0 tw-z-[9998] tw-bg-grey-900 tw-opacity-75' />
      <div
        className='tw-fixed tw-bottom-0 tw-left-0 tw-right-0 tw-top-0 tw-z-[9999] tw-flex tw-min-w-[594px] tw-justify-center tw-p-20'
        data-element-name='projects-modal-modal-wrapper'
        onClick={handleModalWrapperClick}
      >
        <div className='tw-flex tw-h-full tw-max-h-[640px] tw-w-full tw-max-w-[1140px] tw-flex-col tw-overflow-hidden tw-rounded-2xl tw-bg-white'>
          <div className='tw-flex tw-w-full tw-justify-between tw-gap-6 tw-px-5 tw-py-4'>
            <div className='tw-flex tw-w-full tw-items-center tw-justify-between'>
              <div>
                <div className='tw-mb-1 tw-text-l tw-font-semibold tw-tracking-auto tw-text-grey-900'>
                  {title}
                </div>
                <div>{subtitle}</div>
              </div>
              <OutlinedButton
                color='grey'
                disabled={!(projectCount > 0)}
                onClick={() => {
                  if (!isGeneratingExport) onExportClick();
                  setIsGeneratingExportModalOpen(true);
                }}
                size='sm'
              >
                {i18nTShared('export')}
              </OutlinedButton>
            </div>
            <CloseIcon
              className='tw-cursor-pointer tw-rounded-md tw-border-1 tw-border-solid tw-border-white tw-bg-white hover:tw-border-grey-100 [&_polygon]:tw-fill-grey-700'
              data-element-name='projects-modal-close'
              height={24}
              onClick={onClose}
              width={24}
            />
          </div>
          <div
            className='tw-h-full tw-w-full tw-overflow-y-auto tw-overflow-x-hidden'
            data-element-name='projects-modal-table-wrapper'
            onScroll={handleTableWrapperScroll}
          >
            {projectCount === 0 ?
              <div className='tw-flex tw-h-full tw-w-full tw-items-center tw-justify-center tw-p-4 tw-text-center'>
                {i18nT('emptyState', {
                  interpolation: { escapeValue: false },
                  pluralRAMSName,
                })}
              </div>
            : <ProjectsTable
                domainDashboardReport={domainDashboardReport}
                hasNextPage={hasNextPage}
                onProjectClick={onProjectClick}
                selections={selections}
              />
            }
          </div>
        </div>
      </div>
      {isGeneratingExportModalOpen && isGeneratingExport && (
        <GeneratingExportModal
          onClose={() => setIsGeneratingExportModalOpen(false)}
        />
      )}
    </>
  );
}

ProjectsModal.propTypes = {
  domainDashboardReport: PropTypes.shape({
    data: resourceShape('dashboardReport'),
    included: PropTypes.object,
  }),
  hasNextPage: PropTypes.bool,
  isGeneratingExport: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onExportClick: PropTypes.func.isRequired,
  onFetchNextPage: PropTypes.func.isRequired,
  onProjectClick: PropTypes.func.isRequired,
  selections: PropTypes.arrayOf(PropTypes.string).isRequired,
  subtitle: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
};
